<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore, useAppStore } from '@/stores'
import { useToast }     from 'primevue/usetoast'
import Dialog           from 'primevue/dialog'
import { useAppLayout } from '@/use'
import NavSearchInput    from '@/layout/NavSearchInput.vue'
import ReportsMegaMenu   from '@/layout/ReportsMegaMenu.vue'
import ShowAppErrors     from '@/layout/ShowAppErrors.vue'
import MobileBottomNav   from '@/layout/MobileBottomNav.vue'
import ReportLinksDrawer from '@/layout/ReportLinksDrawer.vue'
import AdminLinksDrawer  from '@/layout/AdminLinksDrawer.vue'
import TransfereeForm    from '@/views/transferees/Form.vue'
// CASL disabled — using role helpers (currentUser.isAdmin / isManager) instead.
// import { useAbility }   from '@/use'

import colorLogoWide   from '@/assets/images/logos/trmg-long-color.svg'
import colorLogoSquare from '@/assets/images/logos/trmg-square-color.svg'
import whiteLogoWide   from '@/assets/images/logos/trmg-long-white.svg'
import whiteLogoSquare from '@/assets/images/logos/trmg-square-white.svg'

const toast     = useToast()
const route     = useRoute()
const router    = useRouter()
const authStore = useAuthStore()
const appStore  = useAppStore()

const { isNewTransfereeModalVisible } = storeToRefs(appStore)
const { toggleNewTransfereeModal } = appStore

const { currentUser } = storeToRefs(authStore)
const appLayout = useAppLayout()
const { layoutState } = appLayout

// ---------------------------------------------------------------------------
// Single source of truth for all navigation destinations.
//
// Placement flags (each item can appear in multiple places):
//   sidebar  – desktop sidebar
//   mobile   – mobile nav (first 3 items → tab bar, remainder → "More" sheet)
//   topNav   – desktop top-bar icon strip (managers only)
//
// Item shape:
//   route    – render as <RouterLink> (mutually exclusive with command)
//   command  – render as <button @click>
//   match    – optional extra paths that should highlight this item as active
// ---------------------------------------------------------------------------
const navItems = computed(() => {
  const isManager = currentUser.value.isManager
  return [
    {
      label: 'Tasks',          
      icon: 'ph ph-check-circle',
      route: isManager ? '/' : '/tasks',
      match: isManager ? ['/', '/tasks'] : null,
      sidebar: isManager, mobile: isManager, topNav: false,
    },
    {
      label: 'Tours',          
      icon: 'ph ph-squares-four',
      route: !isManager ? '/': '/tours',
      match: !isManager ? ['/', '/tours'] : null,
      sidebar: true, mobile: true, topNav: false,
    },
    {
      label: 'Calendar',       
      icon: 'ph ph-calendar',
      route: '/calendar',
      sidebar: true, mobile: true, topNav: false,
    },
    {
      label: 'Docs',           
      icon: 'ph ph-file',
      route: '/docs',
      sidebar: isManager, mobile: isManager, topNav: false,
    },
    {
      label: 'Schedule',       
      icon: 'ph ph-calendar-dots',
      route: '/schedule',
      sidebar: false, mobile: isManager, topNav: isManager,
    },
    {
      label: 'New Transferee', 
      icon: 'ph ph-user-circle-plus',
      command: () => toggleNewTransfereeModal(),
      sidebar: false, mobile: isManager, topNav: isManager,
    },
    {
      label: 'Search',         
      icon: 'ph ph-magnifying-glass',
      route: '/search',
      sidebar: false, mobile: isManager, topNav: isManager,
    },
    {
      label: 'divider', divider: true,
      sidebar: isManager, mobile: false, topNav: false,
    },
    {
      label: 'Reports',
      icon: 'ph ph-table',
      command: () => appLayout.toggleReportsDrawer(),
      sidebar: isManager, mobile: isManager, topNav: false,
    },
    {
      label: 'Manage', 
      icon: 'ph ph-list-magnifying-glass',
      command: () => appLayout.toggleAdminDrawer(),
      sidebar: isManager, mobile: isManager, topNav: false,
    },
  ]
})

const sidebarItems   = computed(() => navItems.value.filter(i => i.sidebar))
const bottomNavItems = computed(() => navItems.value.filter(i => i.mobile))
const topNavItems    = computed(() => navItems.value.filter(i => i.topNav))

function isNavItemActive(item) {
  if (!item.route) return false
  return (item.match || [item.route]).includes(appLayout.currentPath)
}

// New Transferee START
const newTransferee = ref({
  initiationDate: dayjs(),
  destinationCounselorId: currentUser.value.dcId,
})

const onSaveTransferee = (response) => {
  toggleNewTransfereeModal()
  router.push(`/transferees/${response.data.transferee.id}`)
}
// New Transferee END

onMounted(() => {
  appLayout.initialize()
})

watch(() => route.path, (val) => appLayout.currentPath = val, { immediate: true })

async function logout() {
  const firstName = currentUser.value?.firstName
  authStore.logout()
  await router.replace({path: '/login'})

  toast.add({
    severity: 'success',
    summary: `Goodbye ${firstName}!`,
    detail: 'You have logged out successfully',
    life: 5000
  });
}

const logoImage = computed(() => {
  return layoutState.isDark ? whiteLogoSquare : colorLogoSquare
  // if (layoutState.showSidebar) {
  //   return layoutState.isDark ? whiteLogoWide : colorLogoWide
  // } else {
  //   return layoutState.isDark ? whiteLogoSquare : colorLogoSquare
  // }
})

const userMenuPopover = ref()
function toggleUserMenu(event) {
  userMenuPopover.value.toggle(event);
}

const isHomePathActive = computed(() =>
  ['/', '/tours'].includes(appLayout.currentPath)
)

const colorSchemeMenu = ref()
watch(() => colorSchemeMenu.value?.visible, (bool) => layoutState.showColorSchemeMenu = bool)

function toggleColorSchemeMenu(event) {
  const isVisible = colorSchemeMenu.value.visible
  if (event.type == 'click') {
    colorSchemeMenu.value.toggle(event)
  } else if (event.type == 'mouseover' && !isVisible) {
    colorSchemeMenu.value.show(event)
  }
}

function applyColorScheme(name) {
  appLayout.prefs.colorScheme = name
}

// Key press listener for Cmd+K / Ctrl+K to focus the search bar
document.addEventListener('keydown', (event) => {
  if (!currentUser.value.isManager) {
    return
  }

  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const modifierKey = isMac ? event.metaKey : event.ctrlKey;

  if (modifierKey && event.key === 'k') {
    event.preventDefault();
    document.getElementById('topNavSearchInput')?.focus();
  }
})
</script>

<template lang="pug">
.app-layout-container

  nav#topnav
    .flex.justify-between
      RouterLink#logo-header(:class="{close: !layoutState.showSidebar}" to="/")
        img.logo-image(:src="logoImage")

      #menu-header.flex
        .left.flex(v-if="!layoutState.isMobile")
          Button#sidebar-toggle-btn.nav-btn(
            @click="appLayout.toggleSidebar"
            :class="{rotate: !layoutState.showSidebar}"
            severity="contrast" text
          )
            AppIcon.sidebar-toggle-icon.text-canvas-600(name="caret-double-left")

          template(v-for="item in topNavItems" :key="item.label")
            .nav-btn(
              v-if="item.command"
              @click="item.command"
              v-tooltip.bottom="{value: item.label, showDelay: 300}"
            )
              i.nav-icon(:class="item.icon")
            RouterLink.nav-btn(
              v-else
              :to="item.route"
              :class="{ 'active-nav': item.route === '/tours' ? isHomePathActive : appLayout.currentPath === item.route }"
              v-tooltip.bottom="{value: item.label, showDelay: 300}"
            )
              i.nav-icon(:class="item.icon")

        .middle.flex-1.flex-around.mx-20(v-if="!layoutState.isMobile && currentUser.isManager")
          .w-full.max-w-2xl
            NavSearchInput

        .right.flex.gap-3.ml-auto
          Button.nav-btn(@mouseleave="toggleColorSchemeMenu" @click="toggleColorSchemeMenu" text severity="contrast")
            i.nav-icon.ph.ph-moon
          Popover(ref="colorSchemeMenu")
            ul
              li: .text-center.my-2 Color Preference
              li: Button.w-full(severity="contrast" text @click="applyColorScheme('light')")
                i.icon.nav-icon.ph(:class="appLayout.prefs.colorScheme=='light' ? 'ph-check text-green-500' : 'ph-sun' ")
                span.nav-text.text-nowrap Light Mode
              li: Button.w-full(severity="contrast" text @click="applyColorScheme('dark')")
                i.icon.nav-icon.ph(:class="appLayout.prefs.colorScheme=='dark' ? 'ph-check text-green-500' : 'ph-moon' ")
                span.nav-text.text-nowrap Dark Mode
              li: Button.w-full(severity="contrast" text @click="applyColorScheme('automatic')")
                i.icon.nav-icon.ph(:class="appLayout.prefs.colorScheme=='automatic' ? 'ph-check text-green-500' : 'ph-user-circle-dashed' ")
                span.nav-text.text-nowrap Automatic

          UserAvatar.nav-icon.cursor-pointer.mr-5(:user="currentUser" @click="toggleUserMenu" style="height: 34px;")
          Popover(ref="userMenuPopover" :pt="{content: {class: 'p-0'}}")
            ul.rounded-lg.overflow-hidden
              li.bg-primary-600(class="p-2.5"): UserAvatar.mx-5.text-white(:user="currentUser" size="large" showFullName="true")
              li: .p-button.p-button-contrast.p-button-text.w-full.py-5.px-2
                RouterLink.w-full.flex-start(to="/settings")
                  i.icon.nav-icon: .ph.ph-user-circle-dashed
                  div.nav-text.text-nowrap.ml-2 User Settings
              li: Button.w-full.py-5.px-2(severity="contrast" text)
                button.cursor-pointer.w-full.flex.gap-2.items-center(@click="logout")
                  AppIcon.nav-icon(name="sign-out")
                  div.nav-text Logout

  //- Left Sidebar
  aside#sidebar(v-if="!layoutState.isMobile" :class="{close: !layoutState.showSidebar}"): #inner-sidebar-grid
    ul.top-area.mt-6
      template(v-for="item in sidebarItems" :key="item.label")
        li.divider(v-if="item.divider")
        li(v-else :class="{ active: isNavItemActive(item) }")
          RouterLink.nav-btn(
            v-if="item.route"
            :to="item.route"
            v-tooltip.right="{value: (!layoutState.showSidebar ? item.label : null), showDelay: 300}"
          )
            i.nav-icon(:class="item.icon")
            span.nav-text {{ item.label }}
          button.nav-btn.w-full.cursor-pointer(
            v-else
            type="button"
            @click="item.command"
            v-tooltip.right="{value: (!layoutState.showSidebar ? item.label : null), showDelay: 300}"
          )
            i.icon.nav-icon(:class="item.icon")
            span.nav-text {{ item.label }}

    .middle-area

    ul.bottom-area.mb-3

  //- Main Body/Page Container
  main
    Toast
    RouterView

  //- Dialog to create new transferee
  Dialog(v-model:visible="isNewTransfereeModalVisible" modal header="Create a New Transferee" :style="{ width: '80vw' }")
    TransfereeForm(:transferee="newTransferee" @save="onSaveTransferee")

  ReportLinksDrawer(:logoImage="logoImage")
  AdminLinksDrawer(:logoImage="logoImage")

  footer.flex.align-center
    ShowAppErrors

  //- Mobile bottom navigation (phones, below md)
  MobileBottomNav(v-if="layoutState.isMobile" :items="bottomNavItems")

</template>
<style lang="scss" scoped>

</style>
